<template>
  <div class="grid-x grid-margin-x">
    <div v-if="isMobile" class="cell title-mobile padding-bottom-xs">
      <ProductEngagementMcom v-if="bestSeller" class="hide-for-medium" is-best-seller />
      <ProductTitle />
      <MarketPlaceTitle v-if="marketPlaceItemEnabled && isMcom" />
    </div>

    <div class="relative" :class="cellLeft">
      <Media />
      <ProductEngagementMcom v-if="productHasEngagementPill" is-pill />
    </div>

    <div :class="cellRight">
      <div v-if="!isMobile" class="title">
        <ProductEngagementMcom v-if="bestSeller" class="show-for-medium" is-best-seller />
        <ProductTitle />
        <MarketPlaceTitle v-if="marketPlaceItemEnabled && isMcom" />
        <ReviewRating />
      </div>
      <ProductTraits>
        <template #shopTheSet>
          <ShopTheSetCarousel v-if="isShopTheSetExpEnabled" />
        </template>
        <template #pzo>
          <Pzo v-if="showPzo && !isProductPerksEnabled" />
        </template>
        <template #shop-more>
          <ShopMoreFacade v-if="isMcom && isShopMoreStandardEnabled" />
        </template>
        <template #ppc>
          <Ppc v-if="!(isMcom && isProductPerksEnabled)" />
        </template>
      </ProductTraits>

      <GWPFacade v-if="isGwpPhase1" data-testid="gwp-facade" />

      <CTAButtons v-if="callToActionButtons" :class="[productHasEngagementPill ? 'margin-bottom-m medium-margin-bottom-z' : 'margin-bottom-m']" />

      <template v-if="isBcom">
        <TransitionExpand
          :should-render-content="shouldRenderAvailability"
          :is-animated="killswitches.isDnFExpandAnimated"
          class="margin-bottom-m"
          data-testid="availability"
        >
          <Availability />
        </TransitionExpand>
        <AvailabilityBigTicket v-if="isBigTicketItemFlag" class="margin-bottom-s" />
        <OnDisplay v-if="isBigTicketItemFlag && showOnDisplay" />
        <LoyalList v-if="isLoyalList" class="margin-bottom-m" />
        <GWPFacade v-if="!isMobile && !isPerksBelowZoneA && isGwpPhase2" data-testid="gwp-facade" />
      </template>

      <ProductEngagementMcom v-if="productHasEngagementPill" class="show-for-medium" />
      <template v-if="isMcom">
        <TransitionExpand
          :should-render-content="shouldRenderAvailability"
          :is-animated="killswitches.isDnFExpandAnimated"
          class="margin-bottom-m"
          data-testid="availability"
        >
          <Availability />
        </TransitionExpand>
        <AvailabilityBigTicket v-if="isBigTicketItemFlag" class="margin-bottom-m" />
        <GWPFacade v-if="!isMobile && !isPerksBelowZoneA && isGwpPhase2" data-testid="gwp-facade" />
        <OnDisplay v-if="isBigTicketItemFlag && showOnDisplay" />
      </template>
      <BTZipCodeSlideout v-if="isBigTicketItemFlag" />
      <template v-if="showShopTheCollection && isMobile">
        <ShopTheCollectionCarousel data-testid="shopthecollection-carousel" class="margin-bottom-m" />
      </template>

      <template v-if="isPerksAboveZoneA && !isMobile">
        <LoyaltyPerksFacade class="cell margin-bottom-m" data-testid="loyalty-perks-section-treatment-1" />
        <ProductPerks data-testid="perks-section-above-pros" />
      </template>

      <div v-if="isPerksBelowZoneA && !isMobile" class="margin-top-m">
        <GWPFacade v-if="isGwpPhase2" class="margin-bottom-m" data-testid="gwp-facade" />
        <LoyaltyPerksFacade class="cell margin-bottom-m" data-testid="loyalty-perks-section-treatment-2" />
        <ProductPerks data-testid="perks-section-below-pros" />
      </div>

      <template v-if="showShopTheCollection && !isMobile">
        <ShopTheCollectionCarousel
          data-testid="shopthecollection-carousel-desktop"
          class="margin-bottom-m"
          :class="{ 'margin-top-m': isPerksBelowZoneA || isPerksAboveZoneA }"
        />
      </template>

      <LazyLoadWhenInViewport>
        <FrequentlyBoughtTogether v-if="prosFBT" />
      </LazyLoadWhenInViewport>
      <LazyLoadWhenInViewport>
        <SponsoredItemTop
          v-if="showSponsoredItemTop"
          :enable-click-tags-for-s-m-products="killswitches.enableClickTagsForSMProducts"
          :is-simplification-mcom-on="killswitches.priceSimplificationEnabled"
        />
      </LazyLoadWhenInViewport>
    </div>
    <template v-if="isBcom">
      <ProsZoneAVertical v-if="!isMobile" data-testid="pros-section" class="cell large-12 margin-top-xl" />
    </template>
    <div class="cell large-12" data-testid="product-details-section">
      <ProductDetails :class="{ 'margin-top-xl': (!isMobile && !showSponsoredItemTop) || prosFBT }" />
    </div>

    <template v-if="isMcom">
      <ProsZoneAVertical v-if="!isMobile" data-testid="pros-section" class="cell large-12 small-margin-top-m large-margin-top-xl" />
    </template>
    <div v-if="isPerksAboveZoneA && isMobile" class="cell margin-top-m">
      <GWPFacade v-if="isGwpPhase2" class="cell margin-bottom-m" data-testid="gwp-facade" />
      <LoyaltyPerksFacade class="margin-bottom-m" data-testid="loyalty-perks-section-treatment-1" />
      <ProductPerks class="cell" data-testid="perks-section-above-pros-mobile" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import SponsoredItemTop from '@nuxt-common/site-monetization/app/components/SponsoredItemTop.vue';
import ProductPerks from '~/modules/productPerks/components/ProductPerks';
import LoyaltyPerksFacade from '~/modules/productPerks/components/LoyaltyPerksFacade';
import OnDisplay from '~/components/common/OnDisplay';
import ProductTitle from '~/components/productTitle/ProductTitle.common';
import ReviewRating from '~/modules/reviews/components/ReviewRating.vue';
import MarketPlaceTitle from '~/components/marketPlace/MarketPlaceTitle';
import Media from '~/components/common/Media/Media';
import ProductEngagementMcom from '~/components/ProductEngagement.mcom';
import CTAButtons from '~/components/ctaButtons/CTAButtons';
import FrequentlyBoughtTogether from '~/modules/pros/FrequentlyBoughtTogether';
import ProductTraits from '~/components/ProductCard/ProductTraits';
import LoyalList from '~/components/LoyalList.bcom';
import LazyLoadWhenInViewport from '~/components/common/LazyLoadWhenInViewport';
import StaticImportProductDetails from '~/modules/productDetails/components/ProductDetails';
import LazyHydratedProductDetails from '~/modules/lazy-hydration/components/ProductDetails';
import ProsZoneAVertical from '~/modules/pros/ProsZoneAVertical';
import ShopMoreFacade from '~/modules/shop-more/components/ShopMoreFacade';
import TransitionExpand from '~/components/common/TransitionExpand';
import useGridLayoutConfig from '~/composables/useGridLayoutConfig';
import ShopTheSetCarousel from '~/modules/shopTheSet/components/ShopTheSetCarousel.vue';

import Availability from '~/modules/availability/components/AvailabilityView';
import AvailabilityBigTicket from '~/modules/availability/components/AvailabilityBigTicket';
import BTZipCodeSlideout from '~/modules/availability/components/slideouts/BTZipCodeSlideout';
import { useAvailability } from '~/modules/availability/composables/useAvailability';
import useGetItTodayExperiments from '~/modules/availability/composables/useGetItTodayExperiments';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useProduct } from '~/modules/product/composables/useProduct';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import Ppc from '~/modules/ppc/components/PPC.vue';
import Pzo from '~/modules/pzo/components/PzoView';
import usePzo from '~/modules/pzo/composables/usePzo';
import GWPFacade from '~/modules/gwp/components/GWPFacade.vue';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';

import { useBrand } from '~/composables/useBrand';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useExperiments from '~/stores/useExperimentsStore';

const selectedPrice = useProductPrice();
const { killswitches } = useKillswitches();

let ProductDetails = StaticImportProductDetails;

// TODO: remove later once lazy hydration testing is over
if (killswitches.isLazyHydrationEnabled) {
  ProductDetails = LazyHydratedProductDetails;
}

const { isBigTicketItemFlag } = useProductFlags();
const {
  productHasEngagementPill,
  bestSeller,
  callToActionButtons,
  prosFBT,
  showSponsoredItemTop,
  isLoyalList,
  marketPlaceItemEnabled,
  basePerksConditions,
  isProductPerksEnabled,
  isProductPerksEnabledPhase2,
  isShopTheSetExpEnabled,
  isShopTheCollectionCarouselEnabled,
} = useFeatureEligibility();
const { isAvailabilityEnabled } = useAvailability();
const { shouldRenderAvailability } = useGetItTodayExperiments(isAvailabilityEnabled);
const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();
const { productData } = useProduct();

const { showPzo } = usePzo();
const {
  experimentFlags: {
    isShopMoreStandardEnabled,
    isSplitPerksEnabled,
    isSplitPerksV2Enabled,
    isSplitPerksV2EnabledSignedIn,
    isGWPEnabled,
    isGWPEnabled2,
  },
} = useExperiments();

const { orderedSiblingProductIds = [] } = productData?.relationships || {};

const showOnDisplay = computed(
  () => (!isMcom && killswitches.isBcomBigTicketSeeItInStoreEnabled) || (isMcom && killswitches.isMcomBigTicketSeeItInStoreEnabled),
);

const { cellLeft, cellRight } = useGridLayoutConfig();

const isPerksAboveZoneAPhase1 = isProductPerksEnabled && isMcom && !isProductPerksEnabledPhase2;
const isPerksAboveZoneAPhase2 = isSplitPerksEnabled && !isSplitPerksV2Enabled && !isSplitPerksV2EnabledSignedIn;

const isPerksAboveZoneA = basePerksConditions && (isPerksAboveZoneAPhase1 || isPerksAboveZoneAPhase2);

const isPerksBelowZoneAPhase1 = isProductPerksEnabled && isBcom && !isProductPerksEnabledPhase2;
const isPerksBelowZoneAPhase2 = (isSplitPerksV2Enabled || isSplitPerksV2EnabledSignedIn) && !isSplitPerksEnabled;
const isPerksBelowZoneA = basePerksConditions && (isPerksBelowZoneAPhase1 || isPerksBelowZoneAPhase2);

const isGwpPhase1 = computed(() => isGWPEnabled && !isGWPEnabled2 && selectedPrice.value.isGWPAvailable);
const isGwpPhase2 = computed(() => !isGWPEnabled && isGWPEnabled2 && selectedPrice.value.isGWPAvailable);
const showShopTheCollection = computed(() => isShopTheCollectionCarouselEnabled && orderedSiblingProductIds?.length > 0);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@include title;
</style>
